import { useState, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import useTabParam from '../hooks/useTabParam';
import { useLanguage } from '../context/LanguageContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ChevronDown, Calculator, RefreshCw, Check, CheckCheck, Eye, History, Info, TrendingDown, Users, CircleCheck, AlertTriangle, AlertOctagon } from 'lucide-react';

const API = '/api/zeitvorgaben-pulse';

const SEVERITY_CONFIG = {
  ok:        { label: 'zvp.ok',        color: '#10B981' },
  anpassung: { label: 'zvp.anpassung', color: '#F59E0B' },
  kritisch:  { label: 'zvp.kritisch',  color: '#EF4444' },
};

const ZV_STATUS_CONFIG = {
  offen:       { labelKey: 'zvp.status.offen',       color: '#9CA3AF', icon: '\u2B55' },
  berechnet:   { labelKey: 'zvp.status.berechnet',   color: '#F59E0B', icon: '\uD83D\uDFE1' },
  geprueft:    { labelKey: 'zvp.status.geprueft',     color: '#3B82F6', icon: '\uD83D\uDFE2' },
  freigegeben: { labelKey: 'zvp.status.freigegeben',  color: '#10B981', icon: '\u2705' },
};

function diffColor(pct) {
  const abs = Math.abs(pct);
  if (abs > 50) return '#EF4444';
  if (abs > 15) return '#F59E0B';
  return '#10B981';
}

function fmt(v, dec = 4) {
  if (v == null) return '\u2014';
  return Number(v).toFixed(dec);
}

const MONAT_NAMEN = ['', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

export default function ZeitvorgabenAnalyse() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useTabParam('analyse');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [monat, setMonat] = useState(null);
  const [jahr, setJahr] = useState(null);
  const [statusFilter, setStatusFilter] = useState('alle');
  const [bewertungFilter, setBewertungFilter] = useState('');
  const [expandedKunde, setExpandedKunde] = useState(null);
  const [historieData, setHistorieData] = useState(null);
  const [historieLoading, setHistorieLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(null);
  const [kommentar, setKommentar] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [analysen, setAnalysen] = useState([]);
  const [search, setSearch] = useState('');
  const [segFilter, setSegFilter] = useState('');
  const [aktivFilter, setAktivFilter] = useState('');
  const [sort, setSort] = useState('name');
  const [direction, setDirection] = useState('asc');
  const [showInactive, setShowInactive] = useState(false);

  // Fetch latest analysis on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/analysen`, { credentials: 'include' });
        const json = await res.json();
        setAnalysen(json);
        // Always use the latest analysis
        if (json.length > 0) {
          setMonat(json[0].kw);
          setJahr(json[0].jahr);
        }
      } catch (err) {
        console.error('Analysen fetch error:', err);
      }
    })();
  }, []);

  function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  const fetchData = useCallback(async (monatVal, jahrVal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (monatVal) params.set('monat', monatVal);
      if (jahrVal) params.set('jahr', jahrVal);
      const res = await fetch(`${API}?${params}`, { credentials: 'include' });
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error('ZVP fetch error:', err);
    } finally {
      setLoading(false);
      setSelected(new Set());
    }
  }, [monat]);

  useEffect(() => { fetchData(monat, jahr); }, [monat, jahr]);

  const fetchHistorie = async (kundeId) => {
    setHistorieLoading(true);
    try {
      const res = await fetch(`${API}/${kundeId}/historie?wochen=12`, { credentials: 'include' });
      const json = await res.json();
      setHistorieData(json);
    } catch (err) {
      console.error('Historie fetch error:', err);
    } finally {
      setHistorieLoading(false);
    }
  };

  const handleExpand = (kundeId) => {
    if (expandedKunde === kundeId) {
      setExpandedKunde(null);
      setHistorieData(null);
    } else {
      setExpandedKunde(kundeId);
      fetchHistorie(kundeId);
    }
  };

  const handleCalculate = async () => {
    if (calculating) return;
    setCalculating(true);
    try {
      await fetch(`${API}/jetzt-berechnen`, { method: 'POST', credentials: 'include' });
      // Refresh analysen list and select the new one
      const res = await fetch(`${API}/analysen`, { credentials: 'include' });
      const newAnalysen = await res.json();
      setAnalysen(newAnalysen);
      if (newAnalysen.length > 0) {
        setMonat(newAnalysen[0].kw);
        setJahr(newAnalysen[0].jahr);
      }
      fetchData(newAnalysen[0]?.kw || null, newAnalysen[0]?.jahr || null);
    } catch (err) {
      console.error('Calculate error:', err);
    } finally {
      setCalculating(false);
    }
  };

  const handlePruefen = async (kundeId) => {
    setActionLoading(true);
    try {
      await fetch(`${API}/${kundeId}/pruefen`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kommentar: kommentar || null }),
      });
      setShowConfirm(null);
      setKommentar('');
      fetchData(monat, jahr);
    } catch (err) {
      console.error('Prüfen error:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleFreigeben = async (kundeId) => {
    setActionLoading(true);
    try {
      const params = new URLSearchParams();
      if (monat) params.set('monat', monat);
      if (jahr) params.set('jahr', jahr);
      await fetch(`${API}/${kundeId}/freigeben?${params}`, {
        method: 'POST', credentials: 'include',
      });
      setShowConfirm(null);
      fetchData(monat, jahr);
    } catch (err) {
      console.error('Freigeben error:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkPruefen = async () => {
    setActionLoading(true);
    try {
      await fetch(`${API}/bulk-pruefen`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kunde_ids: [...selected], kommentar: kommentar || null }),
      });
      setShowConfirm(null);
      setKommentar('');
      setSelected(new Set());
      fetchData(monat, jahr);
    } catch (err) {
      console.error('Bulk prüfen error:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkFreigeben = async () => {
    setActionLoading(true);
    try {
      const params = new URLSearchParams();
      if (monat) params.set('monat', monat);
      if (jahr) params.set('jahr', jahr);
      await fetch(`${API}/bulk-freigeben?${params}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kunde_ids: [...selected] }),
      });
      setShowConfirm(null);
      setSelected(new Set());
      fetchData(monat, jahr);
    } catch (err) {
      console.error('Bulk freigeben error:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSelect = (kundeId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(kundeId)) next.delete(kundeId);
      else next.add(kundeId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(r => r.kunde_id)));
    }
  };

  // Sort helper
  const toggleSort = (col) => {
    if (sort === col) setDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSort(col); setDirection('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sort !== col) return null;
    return <span className="ml-0.5 text-[8px]">{direction === 'asc' ? '▲' : '▼'}</span>;
  };

  // Filtered + sorted rows
  const kunden = data?.kunden || [];

  // Base filter (inactive + search + segment + kundengruppe) — shared for counting
  const baseFiltered = kunden.filter(r => {
    if (!showInactive && (r.zv_status || 'offen') === 'offen' && (!r.anzahl_auftraege_gesamt || Number(r.anzahl_auftraege_gesamt) === 0)) return false;
    if (search && !r.kunde_name.toLowerCase().includes(search.toLowerCase())) return false;
    if (segFilter && (r.segment || '') !== segFilter) return false;
    if (aktivFilter && (r.fulfillment_status || 'unbekannt') !== aktivFilter) return false;
    return true;
  });

  // Count per status tab (respecting bewertung filter + base filters)
  const statusCounts = { alle: 0, offen: 0, berechnet: 0, geprueft: 0, freigegeben: 0 };
  for (const r of baseFiltered) {
    if (bewertungFilter && r.status !== bewertungFilter) continue;
    statusCounts.alle++;
    const st = r.zv_status || 'offen';
    if (statusCounts[st] !== undefined) statusCounts[st]++;
  }

  const filtered = baseFiltered
    .filter(r => {
      // Status filter (workflow)
      if (statusFilter !== 'alle' && (r.zv_status || 'offen') !== statusFilter) return false;
      // Bewertung filter (severity)
      if (bewertungFilter && r.status !== bewertungFilter) return false;
      return true;
    })
    .sort((a, b) => {
      const dir = direction === 'asc' ? 1 : -1;
      switch (sort) {
        case 'name': return dir * (a.kunde_name || '').localeCompare(b.kunde_name || '', 'de');
        case 'segment': return dir * (a.segment || '').localeCompare(b.segment || '');
        case 'auftraege': return dir * ((Number(a.anzahl_auftraege_gesamt) || 0) - (Number(b.anzahl_auftraege_gesamt) || 0));
        case 'sop': return dir * ((Number(a.sop_anteil) || 0) - (Number(b.sop_anteil) || 0));
        case 'diff_pick': return dir * ((Number(a.diff_pick_pct) || 0) - (Number(b.diff_pick_pct) || 0));
        case 'diff_pack': return dir * ((Number(a.diff_pack_pct) || 0) - (Number(b.diff_pack_pct) || 0));
        case 'status': {
          const order = { kritisch: 0, anpassung: 1, ok: 2 };
          return dir * ((order[a.status] ?? 9) - (order[b.status] ?? 9));
        }
        default: return 0;
      }
    });

  const summary = data?.summary || { total: 0, ok: 0, anpassung: 0, kritisch: 0, offen: 0, berechnet: 0, geprueft: 0, freigegeben: 0 };

  return (
    <div className="p-6 lg:p-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm mb-4">
        <NavLink to="/" className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]">Home</NavLink>
        <span className="text-[var(--text-tertiary)]">/</span>
        <span className="text-[var(--text-primary)] font-medium">{t('zvp.title')}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
        <div>
          <h1 className="text-[22px] font-bold text-[var(--text-primary)]">{t('nav.time_targets')}</h1>
          <p className="text-[13px] text-[var(--text-secondary)] mt-1">{t('zvp.subtitle')}</p>
        </div>
        {activeTab === 'analyse' && (
          <div className="flex items-center gap-3 mt-3 lg:mt-0">
            {analysen.length > 0 && (
              <div className="text-right">
                <p className="text-[13px] font-medium text-[var(--text-primary)]">
                  {t('zvp.analyseVom')} {formatDate(analysen[0].berechnet_am)}
                </p>
                <p className="text-[11px] text-[var(--text-tertiary)]">
                  {t('zvp.datenbasis3Monate')}
                </p>
              </div>
            )}

            <button
              onClick={handleCalculate}
              disabled={calculating}
              className="flex items-center gap-2 border border-[var(--border-card,#E5E7EB)] rounded-[10px] px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] bg-[var(--bg-card,#fff)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${calculating ? 'animate-spin' : ''}`} />
              {t('zvp.jetztBerechnen')}
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] gap-[2px] border border-[var(--border-card,#E5E7EB)] mb-6">
        {[
          { key: 'analyse', label: t('zvp.tabs.kundenAnalyse') },
          { key: 'benchmarks', label: t('zvp.tabs.benchmarks') },
        ].map(tb => (
          <button
            key={tb.key}
            onClick={() => setActiveTab(tb.key)}
            className={`px-4 py-[6px] rounded-[7px] text-[13px] transition-colors ${
              activeTab === tb.key
                ? 'bg-[var(--bg-card,#fff)] text-[var(--text-primary)] font-semibold shadow-sm'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {activeTab === 'benchmarks' && <BenchmarksTab t={t} />}

      {activeTab === 'analyse' && <>
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KpiCard label={t('zvp.kunden')} value={summary.total} icon={Users} iconColor="text-[#10B981]" />
        <KpiCard label={t('zvp.ok')} value={summary.ok} badge={{ color: '#10B981', bg: '#D1FAE5' }} icon={CircleCheck} iconColor="text-[#10B981]" />
        <KpiCard label={t('zvp.anpassung')} value={summary.anpassung} badge={{ color: '#F59E0B', bg: '#FEF3C7' }} icon={AlertTriangle} iconColor="text-[#F59E0B]" />
        <KpiCard label={t('zvp.kritisch')} value={summary.kritisch} badge={{ color: '#EF4444', bg: '#FEE2E2' }} icon={AlertOctagon} iconColor="text-[#EF4444]" />
      </div>

      {/* Gesamt-Impact */}
      {summary.gesamt_pk_monat_diff != null && (
        <div className="border border-[var(--border-card,#E5E7EB)] rounded-[16px] px-6 py-4 mb-6 flex flex-wrap items-center gap-x-8 gap-y-2">
          <div className="flex items-center gap-2 text-[12px] font-semibold text-[var(--text-secondary)] uppercase" style={{ letterSpacing: '0.5px' }}>
            <TrendingDown className="w-3.5 h-3.5" />
            {t('zvp.impact.gesamt')}
          </div>
          <div className="text-[13px] text-[var(--text-primary)]">
            <span className="text-[var(--text-secondary)]">{t('zvp.impact.gesamtPkMonat')}:</span>{' '}
            <span className="font-mono">{Number(summary.gesamt_pk_monat_aktuell).toLocaleString('de-DE')}€</span>
            <span className="text-[var(--text-tertiary)] mx-1">→</span>
            <span className="font-mono">{Number(summary.gesamt_pk_monat_neu).toLocaleString('de-DE')}€</span>
            {' '}
            <span className="font-mono font-semibold" style={{ color: summary.gesamt_pk_monat_diff > 0 ? '#F59E0B' : '#10B981' }}>
              ({summary.gesamt_pk_monat_diff > 0 ? '+' : ''}{Number(summary.gesamt_pk_monat_diff).toLocaleString('de-DE')}€)
            </span>
          </div>
          {summary.gesamt_marge_aktuell_pct != null && (
            <div className="text-[13px] text-[var(--text-primary)]">
              <span className="text-[var(--text-secondary)]">{t('zvp.impact.gesamtMarge')}:</span>{' '}
              <span className="font-mono">{summary.gesamt_marge_aktuell_pct}%</span>
              <span className="text-[var(--text-tertiary)] mx-1">→</span>
              <span className="font-mono">{summary.gesamt_marge_neu_pct}%</span>
            </div>
          )}
        </div>
      )}

      {/* Status-Workflow Tabs (primary) */}
      <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] gap-[2px] border border-[var(--border-card,#E5E7EB)] mb-3">
        {[
          { key: 'alle',        label: t('zvp.filter.alle'),           count: statusCounts.alle },
          { key: 'offen',       label: t('zvp.status.offen'),          count: statusCounts.offen },
          { key: 'berechnet',   label: t('zvp.status.berechnet'),      count: statusCounts.berechnet },
          { key: 'geprueft',    label: t('zvp.status.geprueft'),       count: statusCounts.geprueft },
          { key: 'freigegeben', label: t('zvp.status.freigegeben'),    count: statusCounts.freigegeben },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`px-4 py-2 rounded-[8px] text-sm font-medium transition-all ${
              statusFilter === f.key
                ? 'bg-white dark:bg-[var(--bg-card)] text-[var(--text-primary)] font-semibold shadow-sm'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      {/* Secondary filters row */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('timeTargets.searchCustomer')}
          className="px-2.5 py-[7px] rounded-[10px] bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] text-xs text-[var(--text-primary)] w-48 focus:outline-none focus:border-[#338edc]/50"
        />
        <select
          value={bewertungFilter}
          onChange={e => setBewertungFilter(e.target.value)}
          className="px-2.5 py-[7px] rounded-[10px] bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] text-xs text-[var(--text-primary)]"
        >
          <option value="">{t('zeitvorgaben.alleBewertungen')}</option>
          <option value="kritisch">{t('zeitvorgaben.kritisch')}</option>
          <option value="anpassung">{t('zeitvorgaben.anpassungNoetig')}</option>
          <option value="ok">{t('zeitvorgaben.ok')}</option>
        </select>
        <select
          value={segFilter}
          onChange={e => setSegFilter(e.target.value)}
          className="px-2.5 py-[7px] rounded-[10px] bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] text-xs text-[var(--text-primary)]"
        >
          <option value="">{t('timeTargets.allSegments')}</option>
          <option value="A">A</option>
          <option value="B">B</option>
          <option value="C">C</option>
        </select>
        <select
          value={aktivFilter}
          onChange={e => setAktivFilter(e.target.value)}
          className="px-2.5 py-[7px] rounded-[10px] bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] text-xs text-[var(--text-primary)]"
        >
          <option value="">{t('customers.activityStatus')}: {t('customers.alle')}</option>
          <option value="aktiv">{t('customers.fulfillmentAktiv')}</option>
          <option value="ruhend">{t('customers.fulfillmentRuhend')}</option>
          <option value="inaktiv">{t('customers.fulfillmentInaktiv')}</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)}
            className="rounded border-[var(--border-card,#E5E7EB)]"
          />
          {t('zvp.showInactive')}
        </label>

        {/* Bulk actions */}
        {selected.size > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[12px] text-[var(--text-secondary)]">{selected.size} {t('common.selected', 'ausgewählt')}</span>
            <button
              onClick={() => setShowConfirm({ type: 'bulk-pruefen' })}
              className="flex items-center gap-1.5 border border-[var(--border-card,#E5E7EB)] rounded-[10px] px-3 py-1.5 text-[12px] font-medium text-[var(--text-primary)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors"
            >
              <Eye className="w-3 h-3" /> {t('zvp.bulkPruefen')}
            </button>
            <button
              onClick={() => setShowConfirm({ type: 'bulk-freigeben' })}
              className="flex items-center gap-1.5 bg-[#338edc] text-white rounded-[10px] px-3 py-1.5 text-[12px] font-semibold hover:bg-[#2b7bc4] transition-colors"
            >
              <Check className="w-3 h-3" /> {t('zvp.bulkFreigeben')}
            </button>
          </div>
        )}
      </div>

      {/* Main table */}
      {loading ? (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-[16px] p-12 text-center text-[var(--text-secondary)]">
          {t('common.loading', 'Laden...')}
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-[16px] p-12 text-center text-[var(--text-secondary)]">
          {t('common.noData', 'Keine Daten verfügbar')}
        </div>
      ) : (
        <div className="border border-[var(--border-card,#E5E7EB)] rounded-[16px] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border-subtle,#F3F4F6)]">
                <th className="px-2 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={selected.size > 0 && selected.size === filtered.length}
                    onChange={toggleSelectAll}
                    className="rounded border-[var(--border-card,#E5E7EB)]"
                  />
                </th>
                {[
                  { label: 'Kunde', col: 'name' },
                  { label: 'Seg.', col: 'segment' },
                  { label: t('zvp.auftraege', 'Aufträge'), col: 'auftraege' },
                  { label: 'SOP%', col: 'sop' },
                  { label: t('zvp.hinterlegt') + ' Pick' },
                  { label: t('zvp.empfehlung') + ' Pick' },
                  { label: t('zvp.diff') + ' Pick', col: 'diff_pick' },
                  { label: t('zvp.hinterlegt') + ' Pack' },
                  { label: t('zvp.empfehlung') + ' Pack' },
                  { label: t('zvp.diff') + ' Pack', col: 'diff_pack' },
                  { label: 'Status', col: 'status' },
                  { label: t('zvp.impact.margeHeader'), tooltip: t('zvp.impact.margeTooltip') },
                  { label: t('zvp.geprueftVon') },
                  { label: '' },
                ].map((h, i) => (
                  <th key={i}
                      className={`text-left px-3 py-3 ${h.col ? 'cursor-pointer hover:text-[var(--text-primary)]' : ''}`}
                      style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary, #9CA3AF)' }}
                      title={h.tooltip || undefined}
                      onClick={h.col ? () => toggleSort(h.col) : undefined}>
                    {h.label}{h.col && <SortIcon col={h.col} />}
                  </th>
                ))}
              </tr>
            </thead>
            {filtered.map(row => {
              const isExpanded = expandedKunde === row.kunde_id;
              const zvStatus = row.zv_status || 'offen';
              const zvCfg = ZV_STATUS_CONFIG[zvStatus] || ZV_STATUS_CONFIG.offen;
              const severity = row.status ? (SEVERITY_CONFIG[row.status] || SEVERITY_CONFIG.ok) : null;
              const isOffen = zvStatus === 'offen';

              return (
                <tbody key={row.kunde_id}>
                  <tr
                    className="hover:bg-[var(--bg-hover,#F9FAFB)] transition-colors cursor-pointer"
                    style={{ borderBottom: '1px solid var(--border-subtle, #F3F4F6)' }}
                    onClick={() => !isOffen && handleExpand(row.kunde_id)}
                  >
                    <td className="px-2 py-3" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(row.kunde_id)}
                        onChange={() => toggleSelect(row.kunde_id)}
                        className="rounded border-[var(--border-card,#E5E7EB)]"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[13px] font-medium text-[var(--text-primary)]">{row.kunde_name}</span>
                        {row.fulfillment_status && row.fulfillment_status !== 'aktiv' && (() => {
                          const cfg = { ruhend: { bg: '#FEF3C7', text: '#78350F' }, inaktiv: { bg: '#F3F4F6', text: '#374151' } };
                          const c = cfg[row.fulfillment_status] || cfg.inaktiv;
                          const labels = { ruhend: t('customers.fulfillmentRuhend'), inaktiv: t('customers.fulfillmentInaktiv') };
                          return <span className="text-[9px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: c.bg, color: c.text }}>{labels[row.fulfillment_status] || row.fulfillment_status}</span>;
                        })()}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-[13px] text-[var(--text-secondary)] font-mono">{row.segment || '—'}</td>
                    <td className="px-3 py-3 text-[13px] text-[var(--text-primary)] font-mono">
                      {isOffen ? '\u2014' : Number(row.anzahl_auftraege_gesamt).toLocaleString('de-DE')}
                    </td>
                    <td className="px-3 py-3 text-[13px] text-[var(--text-primary)] font-mono">
                      {isOffen ? '\u2014' : `${(Number(row.sop_anteil) * 100).toFixed(0)}%`}
                    </td>
                    {/* Pick */}
                    <td className="px-3 py-3 text-[13px] text-[var(--text-primary)] font-mono">{isOffen ? '\u2014' : fmt(row.hinterlegt_pick_min, 2)}</td>
                    <td className="px-3 py-3 text-[13px] text-[var(--text-primary)] font-mono">{isOffen ? '\u2014' : fmt(row.empfehlung_pick_min, 2)}</td>
                    <td className="px-3 py-3">
                      {isOffen ? '\u2014' : (
                        <span className="font-mono text-[12px] font-semibold" style={{ color: diffColor(Number(row.diff_pick_pct)) }}>
                          {Number(row.diff_pick_pct) > 0 ? '+' : ''}{fmt(row.diff_pick_pct, 1)}%
                        </span>
                      )}
                    </td>
                    {/* Pack */}
                    <td className="px-3 py-3 text-[13px] text-[var(--text-primary)] font-mono">{isOffen ? '\u2014' : fmt(row.hinterlegt_pack_min, 2)}</td>
                    <td className="px-3 py-3 text-[13px] text-[var(--text-primary)] font-mono">{isOffen ? '\u2014' : fmt(row.empfehlung_pack_min, 2)}</td>
                    <td className="px-3 py-3">
                      {isOffen ? '\u2014' : (
                        <span className="font-mono text-[12px] font-semibold" style={{ color: diffColor(Number(row.diff_pack_pct)) }}>
                          {Number(row.diff_pack_pct) > 0 ? '+' : ''}{fmt(row.diff_pack_pct, 1)}%
                        </span>
                      )}
                    </td>
                    {/* Status */}
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-1">
                        {severity && (
                          <span style={{ background: `${severity.color}15`, color: severity.color, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                            {t(severity.label)}
                          </span>
                        )}
                        <span style={{ background: `${zvCfg.color}15`, color: zvCfg.color, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                          {zvCfg.icon} {t(zvCfg.labelKey)}
                        </span>
                      </div>
                    </td>
                    {/* Marge Impact */}
                    <td className="px-3 py-3 text-[12px] font-mono">
                      {isOffen || row.marge_aktuell_pct == null ? '—' : (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="text-[var(--text-primary)]">{row.marge_aktuell_pct}%</span>
                          <span className="text-[var(--text-tertiary)]">→</span>
                          <span className="text-[var(--text-primary)]">{row.marge_neu_pct}%</span>
                          {row.marge_diff_pct != null && row.marge_diff_pct !== 0 && (
                            <span style={{
                              background: row.marge_diff_pct > 0 ? '#10B98118' : '#EF444418',
                              color: row.marge_diff_pct > 0 ? '#10B981' : '#EF4444',
                              padding: '1px 6px', borderRadius: 5, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                            }}>
                              {row.marge_diff_pct > 0 ? '▲' : '▼'} {row.marge_diff_pct > 0 ? '+' : ''}{row.marge_diff_pct}%
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    {/* Geprüft von */}
                    <td className="px-3 py-3 text-[11px] text-[var(--text-secondary)]">
                      {row.zv_geprueft_von_email ? (
                        <div>
                          <div>{row.zv_geprueft_von_email.split('@')[0]}</div>
                          <div className="text-[var(--text-tertiary)]">{row.zv_geprueft_am ? new Date(row.zv_geprueft_am).toLocaleDateString('de-DE') : ''}</div>
                          {row.zv_kommentar && <div className="italic mt-0.5">"{row.zv_kommentar}"</div>}
                        </div>
                      ) : '\u2014'}
                    </td>
                    <td className="px-2 py-3">
                      {!isOffen && (
                        <ChevronDown
                          className="w-6 h-6 text-[var(--text-tertiary)] transition-transform duration-200"
                          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        />
                      )}
                    </td>
                  </tr>

                  {/* Expanded detail */}
                  {isExpanded && !isOffen && (
                    <tr>
                      <td colSpan={15} className="px-4 py-4" style={{ backgroundColor: 'var(--bg-inset, #F8F9FC)' }}>
                        <DetailCard
                          row={row}
                          t={t}
                          historieData={historieData}
                          historieLoading={historieLoading}
                          onPruefen={() => setShowConfirm({ type: 'pruefen', kundeId: row.kunde_id, kundeName: row.kunde_name })}
                          onFreigeben={() => setShowConfirm({ type: 'freigeben', kundeId: row.kunde_id, kundeName: row.kunde_name, row })}
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              );
            })}
          </table>
        </div>
      )}

      </>}

      {/* Confirmation Dialog */}
      {showConfirm && (
        <ConfirmDialog
          type={showConfirm.type}
          kundeName={showConfirm.kundeName}
          row={showConfirm.row}
          kommentar={kommentar}
          setKommentar={setKommentar}
          loading={actionLoading}
          selectedCount={selected.size}
          t={t}
          onConfirm={() => {
            if (showConfirm.type === 'pruefen') handlePruefen(showConfirm.kundeId);
            else if (showConfirm.type === 'freigeben') handleFreigeben(showConfirm.kundeId);
            else if (showConfirm.type === 'bulk-pruefen') handleBulkPruefen();
            else if (showConfirm.type === 'bulk-freigeben') handleBulkFreigeben();
          }}
          onCancel={() => { setShowConfirm(null); setKommentar(''); }}
        />
      )}
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────

function KpiCard({ label, value, badge, icon: Icon, iconColor }) {
  return (
    <div className="border border-[var(--border-card,#E5E7EB)] rounded-[16px] px-6 py-5">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={16} className={iconColor || 'text-[#10B981]'} />}
        <div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)]" style={{ letterSpacing: '0.8px' }}>
          {label}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-1">
        <div className="font-mono text-[28px] font-bold text-[var(--text-primary)]" style={{ letterSpacing: '-0.5px' }}>
          {value}
        </div>
        {badge && (
          <span style={{ background: badge.bg, color: badge.color, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
            {label}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Detail Card ──────────────────────────────────────────

function DetailCard({ row, t, historieData, historieLoading, onPruefen, onFreigeben }) {
  const zvStatus = row.zv_status || 'offen';
  const severity = SEVERITY_CONFIG[row.status] || SEVERITY_CONFIG.ok;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {/* Left: Analysis */}
      <div className="border border-[var(--border-card,#E5E7EB)] rounded-[12px] p-5" style={{ backgroundColor: 'var(--bg-card, #fff)' }}>
        <div className="flex items-center gap-2 mb-4">
          <Calculator className="w-4 h-4 text-[var(--text-secondary)]" />
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            {row.kunde_name} — {MONAT_NAMEN[row.kw] || row.kw} {t('zvp.berechnung')}
          </h3>
        </div>

        {/* Order structure */}
        <div className="mb-4">
          <div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)] mb-2" style={{ letterSpacing: '0.8px' }}>
            {t('zvp.auftragsstruktur')}
          </div>
          <div className="grid grid-cols-2 gap-2 text-[13px]">
            <div className="flex justify-between border-b border-[var(--border-subtle,#F3F4F6)] pb-1">
              <span className="text-[var(--text-secondary)]">{t('zvp.sopAuftraege')}</span>
              <span className="font-mono text-[var(--text-primary)]">{row.anzahl_auftraege_sop} ({(Number(row.sop_anteil) * 100).toFixed(0)}%)</span>
            </div>
            <div className="flex justify-between border-b border-[var(--border-subtle,#F3F4F6)] pb-1">
              <span className="text-[var(--text-secondary)]">{t('zvp.standardAuftraege')}</span>
              <span className="font-mono text-[var(--text-primary)]">{row.anzahl_auftraege_standard}</span>
            </div>
            <div className="flex justify-between border-b border-[var(--border-subtle,#F3F4F6)] pb-1">
              <span className="text-[var(--text-secondary)]">Ø SKUs/Auftrag</span>
              <span className="font-mono text-[var(--text-primary)]">{fmt(row.avg_skus_per_order, 1)}</span>
            </div>
            <div className="flex justify-between border-b border-[var(--border-subtle,#F3F4F6)] pb-1">
              <span className="text-[var(--text-secondary)]">Ø Artikel/Paket</span>
              <span className="font-mono text-[var(--text-primary)]">{fmt(row.avg_articles_per_package, 1)}</span>
            </div>
          </div>
        </div>

        {/* Benchmark table */}
        <div className="mb-4">
          <div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)] mb-2" style={{ letterSpacing: '0.8px' }}>
            {t('zvp.benchmark')}
          </div>
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-[var(--border-subtle,#F3F4F6)]">
                <th className="text-left py-1.5 text-[var(--text-tertiary)] font-semibold uppercase" style={{ fontSize: 10, letterSpacing: '0.5px' }}></th>
                <th className="text-left py-1.5 text-[var(--text-tertiary)] font-semibold uppercase" style={{ fontSize: 10, letterSpacing: '0.5px' }}>SOP</th>
                <th className="text-left py-1.5 text-[var(--text-tertiary)] font-semibold uppercase" style={{ fontSize: 10, letterSpacing: '0.5px' }}>Standard</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[var(--border-subtle,#F3F4F6)]">
                <td className="py-1.5 text-[var(--text-secondary)] font-medium">PICK</td>
                <td className="py-1.5 font-mono text-[var(--text-primary)]">
                  {fmt(row.top25_pick_sop_min, 2)} Min/Auftrag
                  <div className="text-[10px] text-[var(--text-tertiary)]">({row.top25_picker_count} Picker)</div>
                </td>
                <td className="py-1.5 font-mono text-[var(--text-primary)]">
                  {fmt(row.top25_pick_pro_sku_min)} Min/{t('zvp.proSku')}
                  <div className="text-[10px] text-[var(--text-tertiary)]">+ {fmt(row.top25_pick_pro_artikel_min)} Min/{t('zvp.proArtikel')}</div>
                </td>
              </tr>
              <tr>
                <td className="py-1.5 text-[var(--text-secondary)] font-medium">PACK</td>
                <td className="py-1.5 font-mono text-[var(--text-primary)]">
                  {fmt(row.top25_pack_sop_min, 2)} Min/Auftrag
                  <div className="text-[10px] text-[var(--text-tertiary)]">({row.top25_packer_count} Packer)</div>
                </td>
                <td className="py-1.5 font-mono text-[var(--text-primary)]">
                  {fmt(row.top25_pack_basis_min)} Min {t('zvp.basiszeit')}
                  <div className="text-[10px] text-[var(--text-tertiary)]">+ {fmt(row.top25_pack_pro_artikel_min)} Min/{t('zvp.proArtikel')}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Calculation formula */}
        <div className="mb-4">
          <div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)] mb-2" style={{ letterSpacing: '0.8px' }}>
            {t('zvp.berechnung')} (SOP-gewichtet)
          </div>
          <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[8px] p-3 font-mono text-[11px] text-[var(--text-primary)] leading-5">
            <div>Pick: {(Number(row.sop_anteil) * 100).toFixed(0)}% × {fmt(row.top25_pick_sop_min, 2)} + {((1 - Number(row.sop_anteil)) * 100).toFixed(0)}% × [{fmt(row.avg_skus_per_order, 1)} × {fmt(row.top25_pick_pro_sku_min)} + {fmt(row.avg_articles_per_package, 1)} × {fmt(row.top25_pick_pro_artikel_min)}] = <b>{fmt(row.empfehlung_pick_min, 2)}</b> Min</div>
            <div className="mt-1">Pack: {(Number(row.sop_anteil) * 100).toFixed(0)}% × {fmt(row.top25_pack_sop_min, 2)} + {((1 - Number(row.sop_anteil)) * 100).toFixed(0)}% × [{fmt(row.top25_pack_basis_min)} + {fmt(row.avg_articles_per_package, 1)} × {fmt(row.top25_pack_pro_artikel_min)}] = <b>{fmt(row.empfehlung_pack_min, 2)}</b> Min</div>
          </div>
        </div>

        {/* Comparison */}
        <div className="mb-4">
          <div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)] mb-2" style={{ letterSpacing: '0.8px' }}>
            {t('zvp.vergleich')}
          </div>
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-[var(--border-subtle,#F3F4F6)]">
                <th className="text-left py-1.5" style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)' }}></th>
                <th className="text-left py-1.5" style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)' }}>{t('zvp.hinterlegt')}</th>
                <th className="text-left py-1.5" style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)' }}>{t('zvp.empfehlung')}</th>
                <th className="text-left py-1.5" style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)' }}>{t('zvp.diff')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[var(--border-subtle,#F3F4F6)]">
                <td className="py-1.5 text-[var(--text-secondary)]">Pick/Auftrag</td>
                <td className="py-1.5 font-mono text-[var(--text-primary)]">{fmt(row.hinterlegt_pick_min, 2)} Min</td>
                <td className="py-1.5 font-mono text-[var(--text-primary)]">{fmt(row.empfehlung_pick_min, 2)} Min</td>
                <td className="py-1.5">
                  <span className="font-mono font-semibold" style={{ color: diffColor(Number(row.diff_pick_pct)) }}>
                    {Number(row.diff_pick_pct) > 0 ? '+' : ''}{fmt(row.diff_pick_pct, 1)}%
                  </span>
                </td>
              </tr>
              <tr>
                <td className="py-1.5 text-[var(--text-secondary)]">Pack/Auftrag</td>
                <td className="py-1.5 font-mono text-[var(--text-primary)]">{fmt(row.hinterlegt_pack_min, 2)} Min</td>
                <td className="py-1.5 font-mono text-[var(--text-primary)]">{fmt(row.empfehlung_pack_min, 2)} Min</td>
                <td className="py-1.5">
                  <span className="font-mono font-semibold" style={{ color: diffColor(Number(row.diff_pack_pct)) }}>
                    {Number(row.diff_pack_pct) > 0 ? '+' : ''}{fmt(row.diff_pack_pct, 1)}%
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Status message */}
        <div className="rounded-[8px] p-3 text-[12px] mb-4" style={{ backgroundColor: `${severity.color}10`, color: severity.color, border: `1px solid ${severity.color}30` }}>
          {row.status === 'kritisch' && 'KRITISCH: Die hinterlegten Werte weichen stark von der Empfehlung ab. Die FTE-Berechnung im Forecast ist vermutlich deutlich zu hoch oder zu niedrig.'}
          {row.status === 'anpassung' && 'ANPASSUNG EMPFOHLEN: Die Werte weichen merklich ab. Eine Aktualisierung der Stammdaten wird empfohlen.'}
          {row.status === 'ok' && 'OK: Die hinterlegten Werte stimmen gut mit der aktuellen Datengrundlage überein.'}
        </div>

        {/* Impact on Deckungsbeitrag */}
        {row.pk_pro_auftrag_aktuell != null && (
          <div className="mb-4">
            <div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)] mb-2" style={{ letterSpacing: '0.8px' }}>
              {t('zvp.impact.title')}
            </div>
            {/* PK pro Auftrag */}
            <table className="w-full text-[12px] mb-3">
              <thead>
                <tr className="border-b border-[var(--border-subtle,#F3F4F6)]">
                  <th className="text-left py-1.5" style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)' }}>{t('zvp.impact.pkProAuftrag')}</th>
                  <th className="text-right py-1.5" style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)' }}>{t('zvp.impact.aktuell')}</th>
                  <th className="text-right py-1.5" style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)' }}>{t('zvp.impact.nachAenderung')}</th>
                  <th className="text-right py-1.5" style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)' }}>{t('zvp.impact.differenz')}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[var(--border-subtle,#F3F4F6)]">
                  <td className="py-1.5 text-[var(--text-secondary)]">{t('zvp.impact.pkProAuftrag')}</td>
                  <td className="py-1.5 text-right font-mono text-[var(--text-primary)]">{row.pk_pro_auftrag_aktuell}€</td>
                  <td className="py-1.5 text-right font-mono text-[var(--text-primary)]">{row.pk_pro_auftrag_neu}€</td>
                  <td className="py-1.5 text-right">
                    <span className="font-mono font-semibold" style={{ color: row.pk_pro_auftrag_diff > 0 ? '#F59E0B' : '#10B981' }}>
                      {row.pk_pro_auftrag_diff > 0 ? '+' : ''}{row.pk_pro_auftrag_diff}€ ({row.pk_pro_auftrag_diff_pct > 0 ? '+' : ''}{row.pk_pro_auftrag_diff_pct}%)
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="py-1.5 text-[var(--text-secondary)]">{t('zvp.impact.hochrechnung')} (Ø {Number(row.auftraege_pro_monat).toLocaleString('de-DE')} {t('zvp.impact.auftraege')})</td>
                  <td className="py-1.5 text-right font-mono text-[var(--text-primary)]">{Number(row.pk_monat_aktuell).toLocaleString('de-DE')}€</td>
                  <td className="py-1.5 text-right font-mono text-[var(--text-primary)]">{Number(row.pk_monat_neu).toLocaleString('de-DE')}€</td>
                  <td className="py-1.5 text-right">
                    <span className="font-mono font-semibold" style={{ color: row.pk_monat_diff > 0 ? '#F59E0B' : '#10B981' }}>
                      {row.pk_monat_diff > 0 ? '+' : ''}{Number(row.pk_monat_diff).toLocaleString('de-DE')}€
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
            {/* Marge */}
            {row.hat_umsatz ? (
              <div className="grid grid-cols-3 gap-2 text-[12px] mb-3">
                <div className="flex justify-between border-b border-[var(--border-subtle,#F3F4F6)] pb-1">
                  <span className="text-[var(--text-secondary)]">{t('zvp.impact.umsatzProMonat')}</span>
                  <span className="font-mono text-[var(--text-primary)]">{Number(row.umsatz_pro_monat).toLocaleString('de-DE')}€</span>
                </div>
                <div className="flex justify-between border-b border-[var(--border-subtle,#F3F4F6)] pb-1">
                  <span className="text-[var(--text-secondary)]">{t('zvp.impact.margeAktuell')}</span>
                  <span className="font-mono text-[var(--text-primary)]">{row.marge_aktuell_pct}%</span>
                </div>
                <div className="flex justify-between border-b border-[var(--border-subtle,#F3F4F6)] pb-1">
                  <span className="text-[var(--text-secondary)]">{t('zvp.impact.margeNeu')}</span>
                  <span className="font-mono text-[var(--text-primary)]">
                    {row.marge_neu_pct}%{' '}
                    <span className="font-semibold" style={{ color: row.marge_diff_pct < 0 ? '#F59E0B' : '#10B981' }}>
                      ({row.marge_diff_pct > 0 ? '+' : ''}{row.marge_diff_pct}{t('zvp.impact.pp')})
                    </span>
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-[12px] text-[var(--text-tertiary)] italic mb-3">{t('zvp.impact.keinUmsatz')}</div>
            )}
            {/* Hinweis */}
            <div className="flex items-start gap-2 rounded-[8px] p-3 text-[12px]" style={{ backgroundColor: '#338edc10', border: '1px solid #338edc30', color: 'var(--text-secondary)' }}>
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: '#338edc' }} />
              <span>{t('zvp.impact.hinweis')}</span>
            </div>
          </div>
        )}

        {/* Recommended values */}
        <div className="mb-4">
          <div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)] mb-2" style={{ letterSpacing: '0.8px' }}>
            {t('zvp.empfohleneStammdaten')}
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
            <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('zvp.pickProSku')}</span> <span className="font-mono text-[var(--text-primary)]">{fmt(row.empfehlung_pick_pro_sku)} Min</span></div>
            <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('zvp.pickProArtikel')}</span> <span className="font-mono text-[var(--text-primary)]">{fmt(row.empfehlung_pick_pro_artikel)} Min</span></div>
            <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('zvp.packBasiszeit')}</span> <span className="font-mono text-[var(--text-primary)]">{fmt(row.empfehlung_pack_basis)} Min</span></div>
            <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('zvp.packProArtikel')}</span> <span className="font-mono text-[var(--text-primary)]">{fmt(row.empfehlung_pack_pro_artikel)} Min</span></div>
            <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('zvp.sopPickPauschale')}</span> <span className="font-mono text-[var(--text-primary)]">{fmt(row.empfehlung_sop_pick_pauschale)} Min</span></div>
            <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('zvp.sopPackPauschale')}</span> <span className="font-mono text-[var(--text-primary)]">{fmt(row.empfehlung_sop_pack_pauschale)} Min</span></div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          {(zvStatus === 'berechnet' || zvStatus === 'offen') && (
            <>
              <button onClick={onPruefen} className="flex items-center gap-2 border border-[var(--border-card,#E5E7EB)] rounded-[10px] px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
                <Eye className="w-3.5 h-3.5" /> {t('zvp.pruefen')}
              </button>
              <button onClick={onFreigeben} className="flex items-center gap-2 bg-[#338edc] text-white rounded-[10px] px-4 py-2 text-[13px] font-semibold hover:bg-[#2b7bc4] transition-colors">
                <Check className="w-3.5 h-3.5" /> {t('zvp.freigeben')}
              </button>
            </>
          )}
          {zvStatus === 'geprueft' && (
            <button onClick={onFreigeben} className="flex items-center gap-2 bg-[#338edc] text-white rounded-[10px] px-4 py-2 text-[13px] font-semibold hover:bg-[#2b7bc4] transition-colors">
              <Check className="w-3.5 h-3.5" /> {t('zvp.freigeben')}
            </button>
          )}
          {zvStatus === 'freigegeben' && (
            <span className="flex items-center gap-2 text-[13px] text-[#10B981] font-medium">
              <CheckCheck className="w-4 h-4" /> {t('zvp.status.freigegeben')}
              {row.zv_freigegeben_von_email && (
                <span className="text-[var(--text-tertiary)]">
                  ({row.zv_freigegeben_von_email.split('@')[0]}, {new Date(row.zv_freigegeben_am).toLocaleDateString('de-DE')})
                </span>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Right: History chart */}
      <div className="border border-[var(--border-card,#E5E7EB)] rounded-[12px] p-5" style={{ backgroundColor: 'var(--bg-card, #fff)' }}>
        <div className="flex items-center gap-2 mb-4">
          <History className="w-4 h-4 text-[var(--text-secondary)]" />
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            {t('zvp.historieTitel')}
          </h3>
        </div>

        {historieLoading ? (
          <div className="h-[280px] flex items-center justify-center text-[var(--text-secondary)] text-sm">
            {t('common.loading', 'Laden...')}
          </div>
        ) : historieData?.wochen?.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={historieData.wochen.map(w => ({
              name: MONAT_NAMEN[w.kw] || w.kw,
              empfPick: Number(w.empfehlung_pick_min),
              hintPick: Number(w.hinterlegt_pick_min),
              empfPack: Number(w.empfehlung_pack_min),
              hintPack: Number(w.hinterlegt_pack_min),
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle, #F3F4F6)" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-tertiary, #9CA3AF)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--text-tertiary, #9CA3AF)' }} />
              <Tooltip
                contentStyle={{ backgroundColor: 'var(--bg-card, #fff)', border: '1px solid var(--border-card, #E5E7EB)', borderRadius: 8, fontSize: 12 }}
                formatter={(val) => `${Number(val).toFixed(2)} Min`}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="empfPick" name={t('zvp.empfehlung') + ' Pick'} stroke="#338edc" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="hintPick" name={t('zvp.hinterlegt') + ' Pick'} stroke="#338edc" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
              <Line type="monotone" dataKey="empfPack" name={t('zvp.empfehlung') + ' Pack'} stroke="#22D3EE" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="hintPack" name={t('zvp.hinterlegt') + ' Pack'} stroke="#22D3EE" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[280px] flex items-center justify-center text-[var(--text-secondary)] text-sm">
            {t('common.noData', 'Keine Daten verfügbar')}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Confirm Dialog ───────────────────────────────────────

function ConfirmDialog({ type, kundeName, row, kommentar, setKommentar, loading, selectedCount, t, onConfirm, onCancel }) {
  const isPruefen = type === 'pruefen' || type === 'bulk-pruefen';
  const isBulk = type === 'bulk-pruefen' || type === 'bulk-freigeben';

  let title = '';
  if (type === 'pruefen') title = t('zvp.pruefenDialog.title', { kunde: kundeName }) || `Zeitvorgaben prüfen: ${kundeName}`;
  else if (type === 'freigeben') title = t('zvp.freigebenDialog.title', { kunde: kundeName }) || `Freigeben & Übernehmen: ${kundeName}`;
  else if (type === 'bulk-pruefen') title = `${t('zvp.bulkPruefen')}: ${selectedCount} ${t('zvp.kunden')}`;
  else if (type === 'bulk-freigeben') title = `${t('zvp.bulkFreigeben')}: ${selectedCount} ${t('zvp.kunden')}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onCancel}>
      <div className="bg-[var(--bg-card,#fff)] rounded-[16px] border border-[var(--border-card,#E5E7EB)] p-6 w-[480px] max-w-[90vw]" onClick={e => e.stopPropagation()}>
        <h3 className="text-[16px] font-semibold text-[var(--text-primary)] mb-3">{title}</h3>

        {type === 'freigeben' && row && (
          <div className="text-[13px] text-[var(--text-secondary)] mb-4 space-y-2">
            <div>
              <div>Pick: {fmt(row.hinterlegt_pick_min, 2)} Min → <b>{fmt(row.empfehlung_pick_min, 2)} Min</b> ({Number(row.diff_pick_pct) > 0 ? '+' : ''}{fmt(row.diff_pick_pct, 1)}%)</div>
              <div>Pack: {fmt(row.hinterlegt_pack_min, 2)} Min → <b>{fmt(row.empfehlung_pack_min, 2)} Min</b> ({Number(row.diff_pack_pct) > 0 ? '+' : ''}{fmt(row.diff_pack_pct, 1)}%)</div>
            </div>
            {row.pk_pro_auftrag_aktuell != null && (
              <div className="border-t border-[var(--border-subtle,#F3F4F6)] pt-2 text-[12px]">
                <div className="font-semibold text-[var(--text-primary)] mb-1">{t('zvp.impact.title')}:</div>
                <div>{t('zvp.impact.pkProAuftrag')}: {row.pk_pro_auftrag_aktuell}€ → {row.pk_pro_auftrag_neu}€</div>
                <div>{t('zvp.impact.hochrechnung')}: <span className="font-semibold" style={{ color: row.pk_monat_diff > 0 ? '#F59E0B' : '#10B981' }}>{row.pk_monat_diff > 0 ? '+' : ''}{Number(row.pk_monat_diff).toLocaleString('de-DE')}€</span></div>
                {row.hat_umsatz && <div>{t('zvp.impact.margeImpact')}: {row.marge_aktuell_pct}% → {row.marge_neu_pct}%</div>}
              </div>
            )}
            <div className="flex items-start gap-2 rounded-[8px] p-2 text-[11px]" style={{ backgroundColor: '#338edc10', border: '1px solid #338edc30', color: 'var(--text-secondary)' }}>
              <Info className="w-3 h-3 mt-0.5 shrink-0" style={{ color: '#338edc' }} />
              <span>{t('zvp.impact.hinweis')}</span>
            </div>
          </div>
        )}

        {isBulk && (
          <div className="text-[13px] text-[var(--text-secondary)] mb-4">
            {type === 'bulk-pruefen'
              ? `${selectedCount} Kunden als geprüft markieren?`
              : `${selectedCount} Kunden freigeben und Werte in Stammdaten übernehmen?`
            }
          </div>
        )}

        {isPruefen && (
          <div className="mb-4">
            <label className="text-[12px] text-[var(--text-secondary)] mb-1 block">{t('zvp.pruefenDialog.kommentar')}</label>
            <textarea
              value={kommentar}
              onChange={e => setKommentar(e.target.value)}
              className="w-full border border-[var(--border-card,#E5E7EB)] rounded-[10px] px-3 py-2 text-[13px] bg-[var(--bg-card,#fff)] text-[var(--text-primary)]"
              rows={2}
              placeholder={t('zvp.kommentarPlaceholder', 'z.B. "SOP stabil, Standard prüfen"')}
            />
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="border border-[var(--border-card,#E5E7EB)] rounded-[10px] px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button onClick={onConfirm} disabled={loading} className="bg-[#338edc] text-white rounded-[10px] px-4 py-2 text-[13px] font-semibold hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
            {loading ? t('common.loading', 'Laden...') : (
              isPruefen ? t('zvp.pruefenDialog.confirm') : t('zvp.freigebenDialog.confirm')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Benchmarks Tab ──────────────────────────────────────

function BenchmarksTab({ t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [bspSkus, setBspSkus] = useState(5);
  const [bspArtikel, setBspArtikel] = useState(10);
  const [produktivitaet, setProduktivitaet] = useState(70);
  const [savingProd, setSavingProd] = useState(false);
  const [kunden, setKunden] = useState([]);
  const [selectedKundeId, setSelectedKundeId] = useState('');
  const [stundensatz, setStundensatz] = useState(null); // #880: null wenn kein avg verfuegbar
  const [stundensatzIncomplete, setStundensatzIncomplete] = useState(0);

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch('/api/zeitvorgaben/benchmarks/linear', { credentials: 'include' }).then(r => r.json()),
      fetch('/api/einstellungen/fte_effectivity_factor', { credentials: 'include' }).then(r => r.json()).catch(() => null),
      fetch('/api/kunden', { credentials: 'include' }).then(r => r.json()).catch(() => []),
      fetch('/api/lohnkosten/avg-stundensatz', { credentials: 'include' }).then(r => r.json()).catch(() => null),
    ]).then(([bm, ef, kundenData, ss]) => {
      setData(bm);
      if (ef?.value != null) setProduktivitaet(Math.round(parseFloat(ef.value) * 100));
      const aktive = (kundenData || []).filter(k => Number(k.auftraege_30d) > 0).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setKunden(aktive);
      // #880: Durchschnitts-Stundensatz aus jahres_lohnkosten statt default_stundensatz
      if (ss?.stundensatz != null) setStundensatz(parseFloat(ss.stundensatz));
      else setStundensatz(null);
      setStundensatzIncomplete(ss?.anzahl_fehlend || 0);
    }).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCalculate = () => {
    setCalculating(true);
    fetch('/api/zeitvorgaben/benchmarks/calculate', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ period_months: 6 }),
    }).then(() => loadData()).catch(() => {}).finally(() => setCalculating(false));
  };

  const saveProd = () => {
    setSavingProd(true);
    const val = (produktivitaet / 100).toFixed(2);
    fetch('/api/einstellungen/fte_effectivity_factor', {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: val }),
    }).then(() => {}).catch(() => {}).finally(() => setSavingProd(false));
  };

  const selectedKunde = selectedKundeId ? kunden.find(k => String(k.id) === String(selectedKundeId)) : null;

  // When customer selected: use customer coefficients + auto-fill SKUs/articles
  const handleKundeChange = (id) => {
    setSelectedKundeId(id);
    if (id) {
      const k = kunden.find(c => String(c.id) === String(id));
      if (k) {
        if (k.avg_skus_per_order) setBspSkus(Math.round(parseFloat(k.avg_skus_per_order)));
        if (k.avg_articles_per_order) setBspArtikel(Math.round(parseFloat(k.avg_articles_per_order)));
      }
    }
  };

  const pick = data?.pick;
  const pack = data?.pack;

  // Customer-specific or global coefficients
  const useCustomer = selectedKunde && (selectedKunde.pick_pro_sku_min || selectedKunde.pick_pro_artikel_min || selectedKunde.pack_pro_artikel_min);
  const pickProSku = useCustomer ? parseFloat(selectedKunde.pick_pro_sku_min) || 0 : (pick?.pro_sku_min || 0);
  const pickProArtikel = useCustomer ? parseFloat(selectedKunde.pick_pro_artikel_min) || 0 : (pick?.pro_artikel_min || 0);
  const packBasis = useCustomer ? parseFloat(selectedKunde.pack_basis_min) || 0 : (pack?.basis_min || 0);
  const packProArtikel = useCustomer ? parseFloat(selectedKunde.pack_pro_artikel_min) || 0 : (pack?.pro_artikel_min || 0);

  const normalPick = bspSkus * pickProSku + bspArtikel * pickProArtikel;
  const normalPack = packBasis + bspArtikel * packProArtikel;

  // SOP weighting (only when customer selected)
  const sopAnteil = selectedKunde ? parseFloat(selectedKunde.sop_anteil) || 0 : 0;
  const sopPauschale = selectedKunde ? parseFloat(selectedKunde.sop_bulk_pack_pauschale) || 0.75 : 0.75;
  const sopPickZeit = selectedKunde ? parseFloat(selectedKunde.avg_sop_pick_zeit) || 0 : 0;
  const sopPackZeit = selectedKunde ? parseFloat(selectedKunde.avg_sop_pack_zeit) || sopPauschale : sopPauschale;

  const bspPickSoll = selectedKunde && sopAnteil > 0
    ? sopAnteil * sopPickZeit + (1 - sopAnteil) * normalPick
    : normalPick;
  const bspPackSoll = selectedKunde && sopAnteil > 0
    ? sopAnteil * sopPackZeit + (1 - sopAnteil) * normalPack
    : normalPack;

  // Personalkosten pro Auftrag
  const gesamtMin = bspPickSoll + bspPackSoll;
  const pkProAuftrag = (stundensatz != null) ? (gesamtMin / 60) * stundensatz : null;

  const effStunden = (8 * produktivitaet / 100);

  const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-[var(--text-secondary)]">
          {t('timeTargets.linearFormelDesc')}
        </p>
        <button onClick={handleCalculate} disabled={calculating}
          className="flex items-center gap-2 border border-[var(--border-card,#E5E7EB)] rounded-[10px] px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] bg-[var(--bg-card,#fff)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${calculating ? 'animate-spin' : ''}`} />
          {calculating ? t('common.calculating', 'Berechne...') : t('timeTargets.recalculate')}
        </button>
      </div>

      {loading ? (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-[16px] p-12 text-center text-[var(--text-secondary)]">{t('common.loading', 'Laden...')}</div>
      ) : !pick && !pack ? (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-[16px] p-12 text-center text-[var(--text-secondary)]">{t('timeTargets.noBenchmarks')}</div>
      ) : (
        <>
          {/* Formula Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-[var(--border-card,#E5E7EB)] rounded-[16px] p-6">
              <h3 className="text-sm font-medium text-[var(--text-secondary)] mb-4">Pick — {t('timeTargets.linearFormel')}</h3>
              {pick && (
                <>
                  <code className="text-xs text-[#338edc] bg-[#EBF4FD] dark:bg-[#338edc]/10 px-3 py-2 rounded-lg block mb-4 font-mono">
                    SOLL = SKUs × {pick.pro_sku_min}min + Artikel × {pick.pro_artikel_min}min
                  </code>
                  <div className="space-y-2 text-[13px]">
                    <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('timeTargets.proSku')}</span><span className="font-medium text-[var(--text-primary)]">{pick.pro_sku_min} min</span></div>
                    <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('timeTargets.proArtikel')}</span><span className="font-medium text-[var(--text-primary)]">{pick.pro_artikel_min} min</span></div>
                    <div className="flex justify-between text-xs pt-2 border-t border-[var(--border-subtle,#F3F4F6)]">
                      <span className="text-[var(--text-tertiary)]">{t('timeTargets.guete')} (R²)</span><span className="text-[var(--text-tertiary)]">{(pick.r2 * 100).toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between text-xs"><span className="text-[var(--text-tertiary)]">{t('timeTargets.datenpunkte')}</span><span className="text-[var(--text-tertiary)]">{(pick.n || 0).toLocaleString('de-AT')}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-[var(--text-tertiary)]">Top-25% MA</span><span className="text-[var(--text-tertiary)]">{pick.top25_ma || 0}</span></div>
                  </div>
                </>
              )}
            </div>
            <div className="border border-[var(--border-card,#E5E7EB)] rounded-[16px] p-6">
              <h3 className="text-sm font-medium text-[var(--text-secondary)] mb-4">Pack — {t('timeTargets.linearFormel')}</h3>
              {pack && (
                <>
                  <code className="text-xs text-[#338edc] bg-[#EBF4FD] dark:bg-[#338edc]/10 px-3 py-2 rounded-lg block mb-4 font-mono">
                    SOLL = {pack.basis_min}min + Artikel × {pack.pro_artikel_min}min
                  </code>
                  <div className="space-y-2 text-[13px]">
                    <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('timeTargets.basisOverhead')}</span><span className="font-medium text-[var(--text-primary)]">{pack.basis_min} min</span></div>
                    <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('timeTargets.proArtikel')}</span><span className="font-medium text-[var(--text-primary)]">{pack.pro_artikel_min} min</span></div>
                    <div className="flex justify-between text-xs pt-2 border-t border-[var(--border-subtle,#F3F4F6)]">
                      <span className="text-[var(--text-tertiary)]">{t('timeTargets.guete')} (R²)</span><span className="text-[var(--text-tertiary)]">{(pack.r2 * 100).toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between text-xs"><span className="text-[var(--text-tertiary)]">{t('timeTargets.datenpunkte')}</span><span className="text-[var(--text-tertiary)]">{(pack.n || 0).toLocaleString('de-AT')}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-[var(--text-tertiary)]">Top-25% MA</span><span className="text-[var(--text-tertiary)]">{pack.top25_ma || 0}</span></div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Example Calculator */}
          <div className="border border-[var(--border-card,#E5E7EB)] rounded-[16px] p-6">
            <p className="text-sm font-medium text-[var(--text-primary)] mb-4">{t('timeTargets.beispielRechner')}</p>

            {/* Customer Dropdown */}
            <div className="mb-4">
              <label className="text-xs text-[var(--text-tertiary)] block mb-1">{t('timeTargets.calcSelectCustomer')}</label>
              <select value={selectedKundeId} onChange={e => handleKundeChange(e.target.value)}
                className="w-full max-w-xs text-sm border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]">
                <option value="">{t('timeTargets.calcGlobalRegression')}</option>
                {kunden.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </div>

            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="text-xs text-[var(--text-tertiary)] block mb-1">SKUs</label>
                <input type="number" min="1" max="200" value={bspSkus} onChange={e => setBspSkus(Math.max(0, Number(e.target.value) || 0))}
                  className="w-20 text-sm border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 text-center bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
              </div>
              <div>
                <label className="text-xs text-[var(--text-tertiary)] block mb-1">{t('zvp.proArtikel')}</label>
                <input type="number" min="1" max="5000" value={bspArtikel} onChange={e => setBspArtikel(Math.max(0, Number(e.target.value) || 0))}
                  className="w-20 text-sm border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 text-center bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
              </div>
              <div className="text-[13px] text-[var(--text-primary)] flex-1">
                <div className="flex gap-4 flex-wrap">
                  <span>Pick: <strong>{bspPickSoll.toFixed(2)} min</strong>{selectedKunde && sopAnteil > 0 ? ` (${t('timeTargets.calcSopWeighted')})` : ''}</span>
                  <span>Pack: <strong>{bspPackSoll.toFixed(2)} min</strong></span>
                  <span>{t('common.total', 'Gesamt')}: <strong>{gesamtMin.toFixed(2)} min</strong></span>
                </div>
              </div>
            </div>

            {/* SOP info + Personalkosten */}
            <div className="mt-3 space-y-2">
              {selectedKunde && sopAnteil > 0 ? (
                <div className="p-3 bg-[var(--bg-inset,#F8F9FC)] rounded-xl text-xs text-[var(--text-secondary)]">
                  {t('timeTargets.sopShare')}: <strong>{(sopAnteil * 100).toFixed(1)}%</strong>
                  {' · '}{t('timeTargets.sopPauschale')}: <strong>{sopPackZeit.toFixed(2)} min</strong>
                  {useCustomer && <> · <span className="text-[#338edc]">{t('timeTargets.calcCustomerCoeff')}</span></>}
                </div>
              ) : (
                <div className="p-3 bg-[var(--bg-inset,#F8F9FC)] rounded-xl text-xs text-[var(--text-secondary)]">
                  {useCustomer
                    ? <span className="text-[#338edc]">{t('timeTargets.calcCustomerCoeff')}</span>
                    : <>SOP: {t('timeTargets.sopPauschale')} 0,75 min</>
                  }
                </div>
              )}
              <div className="p-3 bg-[var(--bg-inset,#F8F9FC)] rounded-xl text-xs text-[var(--text-secondary)]">
                {pkProAuftrag != null ? (
                  <>
                    {t('timeTargets.calcPkProAuftrag')}: <strong>{pkProAuftrag.toFixed(2)}€</strong>
                    {' '}({t('timeTargets.calcBei')} {stundensatz}€/h)
                    {stundensatzIncomplete > 0 && (
                      <span className="ml-1 text-amber-700" title={t('timeTargets.incompleteLohnkostenTooltip', 'Einige MAs haben noch keine Jahres-Lohnkosten gepflegt — Durchschnitt berechnet sich nur aus gepflegten Werten.')}>
                        ⚠ {stundensatzIncomplete} {t('timeTargets.fehlend', 'fehlend')}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-amber-800">
                    {t('timeTargets.pkNichtBerechenbar', 'Personalkosten nicht berechenbar — keine Jahres-Lohnkosten gepflegt.')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Explanation */}
          <div className="border border-[var(--border-card,#E5E7EB)] rounded-[16px] p-6">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('timeTargets.explTitle')}</h3>

            <h4 className="text-[13px] font-semibold text-[var(--text-primary)] mt-4 mb-2">{t('timeTargets.explOrderTypes')}</h4>

            <div className="mb-4">
              <p className="text-[13px] font-semibold text-[var(--text-primary)] mb-1">{t('timeTargets.explSopTitle')}</p>
              <p className="text-[13px] text-[var(--text-secondary)] mb-1">{t('timeTargets.explSopDesc')}</p>
              <ul className="text-[13px] text-[var(--text-secondary)] list-disc list-inside ml-2 space-y-0.5">
                <li>{t('timeTargets.explSopPick')}</li>
                <li>{t('timeTargets.explSopPack')}</li>
              </ul>
            </div>

            <div className="mb-4">
              <p className="text-[13px] font-semibold text-[var(--text-primary)] mb-1">{t('timeTargets.explStdTitle')}</p>
              <p className="text-[13px] text-[var(--text-secondary)] mb-2">{t('timeTargets.explStdDesc')}</p>

              <p className="text-[13px] font-medium text-[var(--text-primary)] italic mb-1">Picking:</p>
              <p className="text-[13px] text-[var(--text-secondary)] mb-1">{t('timeTargets.explPickDesc')}</p>
              <ul className="text-[13px] text-[var(--text-secondary)] list-disc list-inside ml-2 space-y-0.5 mb-1">
                <li>{t('timeTargets.explPickSku')}</li>
                <li>{t('timeTargets.explPickArt')}</li>
              </ul>
              <p className="text-[13px] text-[var(--text-secondary)] mb-2">
                {t('timeTargets.explFormula')}: <strong className="text-[var(--text-primary)]">SOLL = SKUs × pro_sku_min + Artikel × pro_artikel_min</strong>
              </p>

              <p className="text-[13px] font-medium text-[var(--text-primary)] italic mb-1">Packing:</p>
              <p className="text-[13px] text-[var(--text-secondary)] mb-1">{t('timeTargets.explPackDesc')}</p>
              <ul className="text-[13px] text-[var(--text-secondary)] list-disc list-inside ml-2 space-y-0.5 mb-1">
                <li>{t('timeTargets.explPackBasis')}</li>
                <li>{t('timeTargets.explPackArt')}</li>
              </ul>
              <p className="text-[13px] text-[var(--text-secondary)] mb-2">
                {t('timeTargets.explFormula')}: <strong className="text-[var(--text-primary)]">SOLL = Basis_min + Artikel × pro_artikel_min</strong>
              </p>
            </div>

            <h4 className="text-[13px] font-semibold text-[var(--text-primary)] mt-4 mb-2">{t('timeTargets.explSourceTitle')}</h4>
            <p className="text-[13px] text-[var(--text-secondary)] mb-4">{t('timeTargets.explSourceDesc')}</p>

            <h4 className="text-[13px] font-semibold text-[var(--text-primary)] mt-4 mb-2">{t('timeTargets.explSopWeightTitle')}</h4>
            <p className="text-[13px] text-[var(--text-secondary)] mb-1">{t('timeTargets.explSopWeightDesc')}</p>
            <p className="text-[13px] text-[var(--text-secondary)] mb-4">{t('timeTargets.explSopWeightExample')}</p>

            <h4 className="text-[13px] font-semibold text-[var(--text-primary)] mt-4 mb-2">{t('timeTargets.explWhyDifferent')}</h4>
            <p className="text-[13px] text-[var(--text-secondary)]">{t('timeTargets.explWhyDifferentDesc')}</p>
          </div>

          {/* Productivity Factor */}
          <div className="border border-[var(--border-card,#E5E7EB)] rounded-[16px] p-6">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">{t('timeTargets.effektiveArbeitszeit')}</h3>
            <p className="text-xs text-[var(--text-secondary)] mb-4">{t('timeTargets.effektiveArbeitszeitDesc')}</p>
            <div className="flex items-center gap-3">
              <input type="number" min="50" max="95" step="5" value={produktivitaet}
                onChange={e => setProduktivitaet(Math.max(50, Math.min(95, Number(e.target.value) || 70)))}
                className="w-20 text-sm border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 text-center bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
              <span className="text-sm text-[var(--text-secondary)]">%</span>
              <span className="text-xs text-[var(--text-tertiary)] ml-2">= {effStunden.toFixed(1)}h {t('timeTargets.effektivPro8h')}</span>
              <button onClick={saveProd} disabled={savingProd}
                className="ml-auto border border-[var(--border-card,#E5E7EB)] rounded-[10px] px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] bg-[var(--bg-card,#fff)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors disabled:opacity-50">
                {savingProd ? '...' : t('common.save', 'Speichern')}
              </button>
            </div>
            <div className="mt-3 p-3 bg-[var(--bg-inset,#F8F9FC)] rounded-xl">
              <p className="text-xs text-[var(--text-secondary)]">
                {t('timeTargets.fteBeispiel')}: 1.000 {t('timeTargets.sollStunden')} ÷ {effStunden.toFixed(1)}h
                = <strong>{Math.ceil(1000 / effStunden)} FTE</strong>
                {' '}({t('timeTargets.statt')} {Math.ceil(1000 / 8)} FTE {t('timeTargets.bei100Pct')})
              </p>
            </div>
          </div>

          {data?.last_calculated && (
            <p className="text-xs text-[var(--text-tertiary)]">
              {t('timeTargets.lastCalculated')}: {fmtDate(data.last_calculated)}
              {data.calculated_by ? ` (${data.calculated_by})` : ''}
            </p>
          )}
        </>
      )}
    </div>
  );
}
